import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, errorText } from "@/lib/payments";
import { RecoveryCodes } from "@/components/admin/RecoveryCodes";

const TotpStep = ({ totp }) => (
  <div className="space-y-3">
    <p className="text-sm text-stone-700"><b>Step 2.</b> Open Google Authenticator, Microsoft Authenticator or 1Password on your phone and scan this code.</p>
    <div className="bg-white p-3 inline-block rounded-lg border" data-testid="setup-qr-code"><QRCodeSVG value={totp.otpauth_uri} size={176} /></div>
    <p className="text-xs text-stone-500">Can't scan? Enter this key manually: <span className="font-mono break-all" data-testid="setup-totp-secret">{totp.secret}</span></p>
  </div>
);

export default function AdminSetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [totp, setTotp] = useState(null);
  const [fatal, setFatal] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const v = await axios.post(`${API}/auth/setup/validate`, { token });
        const t = await axios.post(`${API}/auth/setup/totp`, { token });
        setInfo(v.data);
        setTotp(t.data);
      } catch (err) {
        setFatal(errorText(err, "This setup link is invalid."));
      }
    })();
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/setup/complete`, { token, name: form.name, password: form.password, code: form.code });
      setResult(data);
    } catch (err) {
      setError(errorText(err, "Setup failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-sand-200/30 pt-32 md:pt-40 lg:pt-48 pb-20 px-4" data-testid="admin-setup-page">
      <Helmet title="Admin account setup | Prithira Global" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <div className="max-w-lg mx-auto bg-white rounded-2xl border border-stone-200 p-6 sm:p-10 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <h1 className="font-heading text-2xl text-stone-900">{result ? "Save your recovery codes" : "Set up your admin account"}</h1>
        </div>
        {fatal ? <p className="text-red-600" data-testid="setup-error">{fatal}</p>
          : !info ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          : result ? <RecoveryCodes codes={result.recovery_codes} email={info.email} onDone={() => navigate("/admin")} doneLabel="Go to admin dashboard" />
          : (
            <form onSubmit={submit} className="space-y-5">
              <p className="text-sm text-stone-600" data-testid="setup-email">
                Account: <b>{info.email}</b> ({info.role}){info.resets_existing_account && " — this will replace the existing password and authenticator."}
              </p>
              <div className="space-y-3">
                <p className="text-sm text-stone-700"><b>Step 1.</b> Choose your name and a password (at least 12 characters, letters and numbers).</p>
                <div><Label htmlFor="s-name">Your name</Label><Input id="s-name" required value={form.name} onChange={set("name")} data-testid="setup-name-input" /></div>
                <div><Label htmlFor="s-pw">Password</Label><Input id="s-pw" type="password" autoComplete="new-password" required value={form.password} onChange={set("password")} data-testid="setup-password-input" /></div>
                <div><Label htmlFor="s-pw2">Confirm password</Label><Input id="s-pw2" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid="setup-password-confirm-input" /></div>
              </div>
              <TotpStep totp={totp} />
              <div><Label htmlFor="s-code"><b>Step 3.</b> Enter the 6-digit code shown in the app</Label>
                <Input id="s-code" inputMode="numeric" autoComplete="one-time-code" required value={form.code} onChange={set("code")} data-testid="setup-code-input" /></div>
              {error && <p className="text-sm text-red-600" data-testid="setup-form-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full py-6" disabled={busy} data-testid="setup-submit-btn">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
              </Button>
            </form>
          )}
      </div>
    </section>
  );
}
